/* tslint:disable:no-bitwise */

import {
  IFlightMarkersList,
  IAddFlightMarkerRequest,
  ICoords,
} from "./flightMarkersReducer";
import { IGcpCsvRow } from "../modeAddUi/reducer";
import { ISRS } from "../../../apiFacades/csu/site";
import { IFlightMarker } from "../../../apiFacades/bapi/flightMarkers";
import { v4 as uuidv4 } from "uuid";
import { convertLocalToGlobalCoords } from "../../../utils/coordinateConversion/convertLocalToGlobalCoords";

/**
 * Add new nail to this nails list
 * @param nailsList ails list
 * @param payload request with all required data
 * @param srs
 */
export function addNail(
  nailsList: IFlightMarkersList,
  payload: IAddFlightMarkerRequest,
  srs: ISRS,
): IFlightMarkersList {
  const result: IFlightMarkersList = {
    nails: [...(nailsList?.nails ?? [])],
  };

  // 1. Calculate global coordinates
  const globalCoords: ICoords = convertLocalToGlobalCoords(
    payload.localCoords,
    srs,
  );
  // 3. Create INail object from given parameters
  const ts = new Date().getTime();
  const newNail: IFlightMarker = {
    id: generateNailId(),
    name: payload.name,
    operatorName: payload.operatorName,
    operatorCoords: globalCoords,
    tsCreated: ts,
    archivedBy: null,
    tsArchived: null,
    isQC: payload.isQC,
    comment: null,
    commentedBy: null,
    tsCommented: null,
  };
  // 4. Add created nail to list and return it
  result.nails.push(newNail);
  // 5. Return result
  return result;
} // END addNail

export function generateNailId(): string {
  return uuidv4();
} // END generateNailId

/**
 * Creates CSV with content of file for store to S3
 */
export const createScanGCPFileContent = (nailsList: IFlightMarkersList) =>
  nailsList.nails
    .filter((nail: IFlightMarker) => {
      return !nail.isQC && !nail.tsArchived;
    })
    .map((nail: IFlightMarker) => {
      // [GCPName][COORD_1][COORD_2][ALT]
      return `${nail.name}\t${nail.operatorCoords.x}\t${nail.operatorCoords.y}\t${nail.operatorCoords.z}`;
    })
    .join("\n");

export function addNailsFromCSV(
  nailsList: IFlightMarkersList,
  csvList: IGcpCsvRow[],
  operatorName: string,
  srs: ISRS,
): IFlightMarkersList {
  let result = nailsList;
  // If list is not empty - run on it
  for (const row of csvList) {
    // Convert coordinates from string to number
    const xLocal = +row.easting;
    const yLocal = +row.northing;
    const zLocal = +row.elevation;
    // create request to add nail
    const request: IAddFlightMarkerRequest = {
      name: row.name,
      operatorCS: srs,
      operatorName,
      localCoords: {
        x: xLocal,
        y: yLocal,
        z: zLocal,
      },
      isQC: row.type === "QC",
    };
    result = addNail(result, request, srs);
  }
  // After everything is created - return ref to nails list for storing
  return result;
} // END addNailsFromCSV

export function compareNails(
  nail1: IFlightMarker,
  nail2: IFlightMarker,
): number {
  // Comparator
  try {
    // value1 is name of first nail
    const nail1Name = nail1 && nail1.name ? nail1.name : "";
    // value2 is name of second nail
    const nail2Name = nail2 && nail2.name ? nail2.name : "";
    // BEGIN Sophisticated logic for comparing strings and numbers
    // 1. Split names by " "
    const nail1NameSplitted = nail1Name.split(" ");
    const nail2NameSplitted = nail2Name.split(" ");
    // If there is something like "GCP 01" and "GCP 12.4" - get inside
    if (nail1NameSplitted.length <= 2 && nail2NameSplitted.length <= 2) {
      return compareNailNames(nail1Name, nail2Name);
    }
    // END Sophisticated logic for comparing strings and numbers

    if (nail1Name.toLowerCase() < nail2Name.toLowerCase()) {
      return -1;
    }
    if (nail1Name.toLowerCase() > nail2Name.toLowerCase()) {
      return 1;
    }
  } catch (e) {
    // Can't sort, put it on top
    return 1;
  }
  return 0;
} // END comapreNails

export function compareNailNames(nail1Name: string, nail2Name: string): number {
  const nail1NameSplitted: string[] = nail1Name.split(" ");
  const nail2NameSplitted: string[] = nail2Name.split(" ");
  if (nail1NameSplitted.length === 1 && nail2NameSplitted.length === 1) {
    const nail1Number: number = +nail1NameSplitted[0];
    const nail2Number: number = +nail2NameSplitted[0];
    if (nail1Number > 0 && nail2Number > 0) {
      return nail1Number - nail2Number;
    }
  }
  const nail1NameStringPart = nail1NameSplitted[0];
  const nail2NameStringPart = nail2NameSplitted[0];
  if (nail1NameStringPart.toLowerCase() < nail2NameStringPart.toLowerCase()) {
    return -1;
  }
  if (nail1NameStringPart.toLowerCase() > nail2NameStringPart.toLowerCase()) {
    return 1;
  }
  // string parts are equal, try to convert numeric parts and compare them
  const nail1NumberPart = +nail1NameSplitted[1];
  const nail2NumberPart = +nail2NameSplitted[1];
  if (nail1NumberPart < nail2NumberPart) {
    return -1;
  }
  if (nail1NumberPart > nail2NumberPart) {
    return 1;
  }
  return 0;
} // END compareNailNames

export function WGS84(): ISRS {
  return {
    assumedSrs: null,
    globalSrs: {
      epsg: "EPSG:4326",
      proj4: "+proj=longlat +ellps=WGS84 +datum=WGS84 +no_defs ",
      name: "WGS84",
    },
  };
} // END WGS84

export function extractNailNumberFromName(name: string): number {
  const res = name.match(/(\d+)/);
  if (!res) {
    return 0;
  }
  return parseInt(res[0], 10);
}

export function getNextNailNumber(nails: IFlightMarker[]): number {
  if (!nails.length) {
    return 1;
  }
  return (
    nails
      .map(({ name }) => extractNailNumberFromName(name))
      .sort((a, b) => b - a)[0] + 1
  );
}
